import type { OrganisationResponseRow } from '@risksmart-app/drizzle/src/types';
import type { GetOrganisationQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetOrganisationDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, REFERENCE_DATA_STALE_TIME_MS } from 'src/utils';

export const useGetOrganisation = createQueryHook<
  Record<string, never>,
  OrganisationResponseRow[],
  GetOrganisationQuery
>({
  trpcQueryOptions: (trpc) => trpc.frontend.organisation.current.queryOptions(),
  mapTrpcDataToGraphQL: (data) => ({ auth_organisation: data }),
  // Org-level administrative config — changes on the order of days (COI-882).
  // Factory-level, so it also covers the SSO tab; that call site refetches
  // straight after its own SCIM write, so an admin still sees their change at
  // once. The window only governs how fast OTHER sessions notice.
  trpcStaleTime: REFERENCE_DATA_STALE_TIME_MS,
  graphqlDocument: GetOrganisationDocument,
});

/**
 * Feature evaluation needs the organisation before anything downstream can
 * select a transport. The read stays behind this seam so `FeaturesProvider`
 * can hand the result to `FeaturesProviderView` as props — that is what makes
 * the provider testable without mounting a transport.
 *
 * `cache-first` preserves the policy this read had under Apollo's default; the
 * factory defaults to `no-cache`. Kept as a per-call override rather than a
 * factory default so the SSO settings tab's GraphQL leg is untouched.
 */
export const useGetOrganisationForFeatureBootstrap = () =>
  useGetOrganisation({ queryArgs: {}, fetchPolicy: 'cache-first' });
